import type { AccessContext, AccessDecision, AccessPermissionKey, AccessRoleAssignment } from './accessModel.ts';

const normalize = (value: string) => value.trim().toLowerCase();

export function assignedRoleScopes(context: AccessContext): AccessRoleAssignment[] {
  if (context.roleScopeVersion !== 3) return context.assignments;
  return (context.roleMemberships ?? []).flatMap((membership) => {
    const role = context.roles.find((entry) => entry.roleId === membership.roleId);
    return (role?.pslIds ?? []).map((pslId) => ({ ...membership, assignmentId: `${membership.assignmentId}:${pslId}`, pslId }));
  });
}

export function evaluateAccess(
  context: AccessContext,
  permissionKey: AccessPermissionKey,
  recordPslId: string | null,
): AccessDecision {
  const permission = context.permissions.find((entry) => entry.permissionKey === permissionKey);
  if (!permission) return { allowed: false, permissionKey, recordPslId, matchingRoles: [], reason: 'unknown-permission' };
  if (context.permissionGrantVersion === 4) {
    const requestedPsl = recordPslId ? context.psls.find((psl) => psl.pslId === recordPslId && psl.isActive) : undefined;
    if (permission.scopeRule === 'record' && (!recordPslId || !requestedPsl)
      || permission.scopeRule === 'any-psl' && recordPslId && !requestedPsl
      || permission.scopeRule === 'global-only' && recordPslId && !requestedPsl) {
      return { allowed: false, permissionKey, recordPslId, matchingRoles: [], reason: 'no-assignment' };
    }
    const membershipRoleIds = new Set((context.roleMemberships ?? []).filter((membership) => membership.userId === context.userId).map((membership) => membership.roleId));
    if (permission.scopeRule === 'global-only') {
      const activeBusinessPsls = context.psls.filter((psl) => psl.isActive && !psl.isGlobal);
      const roleById = new Map(context.roles.filter((role) => role.isActive && membershipRoleIds.has(role.roleId)).map((role) => [role.roleId, role]));
      const grantingRoleIds = new Set<string>();
      const fullyGranted = activeBusinessPsls.length > 0 && activeBusinessPsls.every((psl) => {
        const grants = (context.rolePermissionGrants ?? []).filter((grant) => grant.permissionId === permissionKey && grant.pslId === psl.pslId
          && roleById.has(grant.roleId));
        for (const grant of grants) grantingRoleIds.add(grant.roleId);
        return grants.length > 0;
      });
      const matchingRoles = [...grantingRoleIds].flatMap((roleId) => roleById.get(roleId)?.roleName ?? []);
      if (fullyGranted) return { allowed: true, permissionKey, recordPslId, matchingRoles, reason: 'granted' };
      return { allowed: false, permissionKey, recordPslId, matchingRoles, reason: membershipRoleIds.size ? 'permission-not-granted' : 'no-assignment' };
    }
    const matchingRoles = context.roles.filter((role) => role.isActive && membershipRoleIds.has(role.roleId)
      && context.rolePermissionGrants?.some((grant) => grant.roleId === role.roleId
        && grant.permissionId === permissionKey && (grant.pslId === recordPslId
          || permission.scopeRule === 'any-psl' && recordPslId === null
            && context.psls.some((psl) => psl.pslId === grant.pslId && psl.isActive && !psl.isGlobal)))).map((role) => role.roleName);
    if (matchingRoles.length) return { allowed: true, permissionKey, recordPslId, matchingRoles, reason: 'granted' };
    const hasMembership = membershipRoleIds.size > 0;
    return { allowed: false, permissionKey, recordPslId, matchingRoles: hasMembership
      ? context.roles.filter((role) => role.isActive && membershipRoleIds.has(role.roleId)).map((role) => role.roleName) : [],
    reason: hasMembership ? 'permission-not-granted' : 'no-assignment' };
  }
  if (context.roleScopeVersion !== 3 && context.assignmentScopeVersion !== 2 && context.profilePslIds && permission.scopeRule === 'record' && recordPslId) {
    const globalProfile = context.psls.some((psl) => psl.isActive && psl.isGlobal && context.profilePslIds?.includes(psl.pslId));
    if (!globalProfile && !context.profilePslIds.includes(recordPslId)) {
      return { allowed: false, permissionKey, recordPslId, matchingRoles: [], reason: 'no-assignment' };
    }
  }

  const candidates = assignedRoleScopes(context).filter((assignment) => {
    if (assignment.userId !== context.userId) return false;
    const psl = context.psls.find((entry) => entry.pslId === assignment.pslId);
    if (!psl || !psl.isActive) return false;
    if (permission.scopeRule === 'global-only') return psl.isGlobal;
    if (permission.scopeRule === 'any-psl') return true;
    if (recordPslId === null) return false;
    return assignment.pslId === recordPslId || psl.isGlobal;
  });
  if (!candidates.length) return { allowed: false, permissionKey, recordPslId, matchingRoles: [], reason: 'no-assignment' };

  const activeRoles = candidates.flatMap((assignment) => {
    const role = context.roles.find((entry) => entry.roleId === assignment.roleId);
    return role?.isActive ? [{ assignment, role }] : [];
  });
  if (!activeRoles.length) return { allowed: false, permissionKey, recordPslId, matchingRoles: [], reason: 'inactive-role' };

  const grantsPermission = (roleId: string, pslId: string) => {
    const override = context.rolePslPermissions.find((entry) => entry.roleId === roleId
      && entry.pslId === pslId && entry.permissionKey === permissionKey);
    if (override) return override.effect === 'allow';
    return context.rolePermissions.some((entry) => entry.roleId === roleId && entry.permissionKey === permissionKey);
  };
  const matchingRoles = activeRoles.filter(({ assignment, role }) => {
    const pslId = permission.scopeRule === 'global-only' ? context.psls.find((psl) => psl.isGlobal)?.pslId
      : recordPslId ?? assignment.pslId;
    return pslId ? grantsPermission(role.roleId, pslId) : false;
  }).map(({ role }) => role.roleName);
  if (matchingRoles.length) return { allowed: true, permissionKey, recordPslId, matchingRoles, reason: 'granted' };

  return { allowed: false, permissionKey, recordPslId, matchingRoles: activeRoles.map(({ role }) => role.roleName), reason: 'permission-not-granted' };
}

export function permissionScope(context: AccessContext, permissionKey: AccessPermissionKey): { global: boolean; pslIds: Set<string> } {
  if (context.permissionGrantVersion === 4) {
    const pslIds = new Set<string>();
    const grants = context.rolePermissionGrants ?? [];
    const roles = new Set((context.roleMemberships ?? []).filter((membership) => membership.userId === context.userId).map((membership) => membership.roleId));
    for (const psl of context.psls) if (psl.isActive && !psl.isGlobal && grants.some((grant) => roles.has(grant.roleId)
      && grant.permissionId === permissionKey && grant.pslId === psl.pslId)
      && evaluateAccess(context, permissionKey, psl.pslId).allowed) pslIds.add(psl.pslId);
    const businessCount = context.psls.filter((psl) => psl.isActive && !psl.isGlobal).length;
    const permission = context.permissions.find((entry) => entry.permissionKey === permissionKey);
    const global = businessCount > 0 && pslIds.size === businessCount;
    if (permission?.scopeRule === 'global-only') return { global, pslIds: new Set() };
    return { global, pslIds };
  }
  if (context.roleScopeVersion === 3) return permissionScope({ ...context, roleScopeVersion: undefined,
    assignments: assignedRoleScopes(context), assignmentScopeVersion: 2, profilePslIds: undefined }, permissionKey);
  const pslIds = new Set<string>();
  const permission = context.permissions.find((entry) => entry.permissionKey === permissionKey);
  if (!permission) return { global: false, pslIds };
  const activePsls = context.psls.filter((psl) => psl.isActive);
  const globalPsl = activePsls.find((psl) => psl.isGlobal);
  const legacyProfilePsls = context.assignmentScopeVersion === 2 ? undefined : context.profilePslIds;
  const globalProfile = !legacyProfilePsls || !!globalPsl && legacyProfilePsls.includes(globalPsl.pslId);
  const inPsl = (pslId: string) => ({ ...context, assignments: context.assignments.filter((assignment) =>
    assignment.pslId === pslId || assignment.pslId === globalPsl?.pslId) });
  const globalAllowed = !!globalPsl && globalProfile && evaluateAccess({ ...context,
    assignments: context.assignments.filter((assignment) => assignment.pslId === globalPsl.pslId),
  }, permissionKey, globalPsl.pslId).allowed;
  if (permission.scopeRule === 'global-only') return { global: globalAllowed, pslIds };
  const businessPsls = activePsls.filter((psl) => !psl.isGlobal);
  for (const psl of businessPsls) {
    if (!globalProfile && !legacyProfilePsls?.includes(psl.pslId)) continue;
    if (evaluateAccess(inPsl(psl.pslId), permissionKey, psl.pslId).allowed) pslIds.add(psl.pslId);
  }
  return { global: globalAllowed && pslIds.size === businessPsls.length, pslIds };
}

export function effectivePermissions(context: AccessContext, recordPslId: string | null): AccessPermissionKey[] {
  return context.permissions
    .filter((permission) => evaluateAccess(context, permission.permissionKey, recordPslId).allowed)
    .map((permission) => permission.permissionKey);
}

export function explainAccess(decision: AccessDecision, pslName: string): string[] {
  if (decision.reason === 'unknown-permission') return [`${decision.permissionKey} is not in the permission catalogue.`];
  if (decision.reason === 'no-assignment') return [`No active role assignment applies to ${pslName}.`];
  if (decision.reason === 'inactive-role') return [`The matching role assignment is inactive for ${pslName}.`];
  if (decision.allowed) return [`${decision.permissionKey} is granted by ${decision.matchingRoles.join(', ')} in ${pslName}.`];
  return [`Roles in scope (${decision.matchingRoles.join(', ')}) do not grant ${decision.permissionKey} in ${pslName}.`];
}

export function permissionKeysForRole(context: AccessContext, roleId: string): AccessPermissionKey[] {
  if (context.permissionGrantVersion === 4) return [...new Set((context.rolePermissionGrants ?? [])
    .filter((entry) => normalize(entry.roleId) === normalize(roleId)).map((entry) => entry.permissionId))];
  return context.rolePermissions
    .filter((entry) => normalize(entry.roleId) === normalize(roleId))
    .map((entry) => entry.permissionKey);
}